import React, { useState, useRef } from 'react';
import NDVIChart from '../Monitoring/NDVIChart';
import {
    GoogleMap,
    useLoadScript,
    Marker,
    Polygon,
    InfoWindow
} from '@react-google-maps/api';

const containerStyle = {
    width: '100%',
    height: '600px',
    borderRadius: '8px',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
};
//const defaultCenter = { lat: 18.74407, lng: 73.9700741 };
const defaultCenter = { lat: 22.5937, lng: 78.9629 };

export const AgriFieldMap = () => {
    const { isLoaded } = useLoadScript({
        // googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY
         //googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || "AIzaSyDmmBMzFbZmGwc3FMR90OQNy8K3WFFK9eU",
         googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || "AIzaSyC16Y-R8bhKWx57M63qEGelyGB3SRfxFIU",
    });

    const inputRef = useRef();
    const polygonRef = useRef(null);

    const [center, setCenter] = useState(defaultCenter);
    const [marker, setMarker] = useState(null);
    const [fieldId, setFieldId] = useState(null);
    const [fieldPolygon, setFieldPolygon] = useState([]);
    const [amedData, setAmedData] = useState(null);
    const [mapType, setMapType] = useState("satellite");
    const [infoWindowPos, setInfoWindowPos] = useState(null);
    const [clickedAddress, setClickedAddress] = useState('');

    // ✅ Geocode place using Nominatim
    const geocodePlace = async () => {
        const place = inputRef.current.value;
        if (!place) return alert("Please enter a place");

        const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(place)}&limit=1&countrycodes=in`;

        try {
            const response = await fetch(url);
            const data = await response.json();
            if (data.length > 0) {
                const location = {
                    lat: parseFloat(data[0].lat),
                    lng: parseFloat(data[0].lon)
                };
                setCenter(location);
                setMarker(location);
            } else {
                alert("Location not found");
            }
        } catch (error) {
            alert("Failed to fetch location");
            console.error(error);
        }
    };

    // ✅ Click on map: Fetch or simulate field
    const handleMapClick = async (event) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        const clickedLocation = { lat, lng };

        setMarker(clickedLocation);
        setCenter(clickedLocation);
        setInfoWindowPos(clickedLocation);
        setClickedAddress("Loading...");

        // Reverse geocode using OpenStreetMap
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`
            );
            const data = await response.json();
            setClickedAddress(data.display_name || "Address not found");
        } catch (error) {
            console.error("Reverse geocode failed", error);
            setClickedAddress("Address not found");
        }
        // Simulate square polygon around clicked point
        const buffer = 0.0004;
        const polygon = [
            { lat: lat + buffer, lng: lng - buffer },
            { lat: lat + buffer, lng: lng + buffer },
            { lat: lat - buffer, lng: lng + buffer },
            { lat: lat - buffer, lng: lng - buffer }
        ];

        setFieldPolygon(polygon);
        setFieldId(`CLICKED-${Date.now()}`); // temporary ID

        // Optional: clear previous AMED data
        setAmedData(null);
        // MOCK: Replace with real ALU API call
        const aluField = await getMockALUFieldID(lat, lng);
        setFieldId(aluField.id);
        setFieldPolygon(aluField.geometry);

        // 🔁 MOCK: Replace with real AMED API call
        const monitoring = await getMockAMEDData(aluField.id);
        setAmedData(monitoring);
    };

    // ✅ Polygon load (store reference)
    const handlePolygonLoad = (polygon) => {
        polygonRef.current = polygon;
    };

    // ✅ When polygon is edited (drag/drop)
    const handlePolygonEdited = () => {
        const polygon = polygonRef.current;
        if (!polygon) return;

        const path = polygon.getPath();
        const updatedPath = path.getArray().map(coord => ({
            lat: coord.lat(),
            lng: coord.lng()
        }));

        setFieldPolygon(updatedPath);
        console.log("Polygon updated:", updatedPath);
    };

    // 🔁 MOCK ALU function
    const getMockALUFieldID = async (lat, lng) => {
        return {
            id: "FIELD_MOCK_001",
            geometry: [
                { lat: lat + 0.0004, lng: lng - 0.0004 },
                { lat: lat + 0.0004, lng: lng + 0.0004 },
                { lat: lat - 0.0004, lng: lng + 0.0004 },
                { lat: lat - 0.0004, lng: lng - 0.0004 }
            ]
        };
    };

    // 🔁 MOCK AMED function
    const getMockAMEDData = async (fieldId) => {
        return {
            fieldId,
            cropType: "Soybean",
            cropStage: "Flowering",
            ndvi: 0.76,
            soilMoisture: "Moderate",
            lastUpdated: "2025-09-14",
            ndviHistory: [
                { date: "2025-08-01", ndvi: 0.52 },
                { date: "2025-08-08", ndvi: 0.58 },
                { date: "2025-08-15", ndvi: 0.61 },
                { date: "2025-08-22", ndvi: 0.70 },
                { date: "2025-08-29", ndvi: 0.74 },
                { date: "2025-09-05", ndvi: 0.76 },
            ],
        };
    };


    /*
    ✅ Replace MOCK with Google Agri ALU API

    const getALUFieldID = async (lat, lng) => {
        const url = `https://agriculture.googleapis.com/v1/fields:searchByPoint?key=YOUR_API_KEY`;
        const body = {
            location: {
                latitude: lat,
                longitude: lng
            }
        };
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
                // "Authorization": "Bearer YOUR_ACCESS_TOKEN"
            },
            body: JSON.stringify(body)
        });
        const data = await response.json();
        if (!data.fields || data.fields.length === 0) return null;
        const field = data.fields[0];
        const geometry = field.geometry.linearRing.map(pt => ({
            lat: pt.latitude,
            lng: pt.longitude
        }));
        return {
            id: field.name.split("/").pop(),
            geometry
        };
    };
    */

    /*
    ✅ Replace MOCK with Google Agri AMED API

    const getAMEDMonitoring = async (fieldId) => {
        const projectId = "YOUR_PROJECT_ID";
        const fullFieldName = `projects/${projectId}/locations/global/fields/${fieldId}`;
        const url = `https://agriculture.googleapis.com/v1/projects/${projectId}/locations/global/fieldEvents:get?field=${encodeURIComponent(fullFieldName)}&key=YOUR_API_KEY`;
        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json"
                // "Authorization": "Bearer YOUR_ACCESS_TOKEN"
            }
        });
        const data = await response.json();
        return {
            fieldId,
            cropType: data.cropType || "Unknown",
            cropStage: data.cropStage || "Unknown",
            ndvi: data.ndvi || 0,
            soilMoisture: data.soilMoisture || "Unknown",
            lastUpdated: data.lastUpdated || new Date().toISOString()
        };
    };
    */

    return (
        <div style = {{ fontFamily: "Segoe UI, sans-serif" }
}>
    {/* ✅ Page Header */ }
    < div style = {{
    backgroundColor: "#fff",
        color: "#000",
            padding: "15px 20px",
                fontSize: "22px",
                    fontWeight: "bold",
                        borderBottom: "4px solid #99cc22"
}}>
                🌾 Smart Agriculture Dashboard – Field Mapping & Monitoring
            </div >

    {/* ✅ Main Panel */ }
    < div style = {{
    display: "flex",
        flexDirection: "row",
            padding: "10px",
                gap: "10px"
}}>
    {/* Map Panel */ }
    <div style = {{ flex: 3 }}>
        <div style={{ marginBottom: 10 }} class="row">
        <div class="col">
            <input
                ref={inputRef}
                type="text"
                placeholder="Enter village, city, or coordinates"
                style={{
                    width: "100%",
                    padding: "8px",
                    fontSize: "14px",
                    marginRight: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ccc"
                }}
                            />
        </div>
        <div class="col">
            <button
                onClick={geocodePlace}
                style={{
                    padding: "8px 12px",
                    fontSize: "14px",
                    backgroundColor: "#99cc22",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer"
                }}
            >
                🔍 Search
                            </button>
                        </div>
        
        
                        <div class="col">
                            <button
                            onClick={() => setMapType("satellite")}
                            style={{
                                padding: "8px 10px",
                                fontSize: "14px",
                                backgroundColor: mapType === "satellite" ? "#2980b9" : "#bdc3c7",
                                color: "#fff",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer"
                            }}
  >
                            🛰️ Satellite
                        </button>
                        </div>
                        <div class="col">
                        <button
                            onClick={() => setMapType("roadmap")}
                            style={{
                                padding: "8px 10px",
                                fontSize: "14px",
                                backgroundColor: mapType === "roadmap" ? "#2980b9" : "#bdc3c7",
                                color: "#fff",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer"
                            }}
                        >
                            🗺️ Roadmap
                        </button>
                        </div>
        </div>

{
    isLoaded ? (
        <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={18}
            mapTypeId={mapType}

            onClick={handleMapClick}
        >
                                {marker && <Marker position={marker} />}
                                {infoWindowPos && (
                                    <InfoWindow
                                        position={infoWindowPos}
                                        onCloseClick={() => {
                                            setInfoWindowPos(null);
                                            setClickedAddress('');
                                        }}
                                    >
                                        <div style={{ maxWidth: "250px" }}>
                                            <strong>📍 Address:</strong><br />
                                            <small>{clickedAddress}</small>
                                            <hr />
                                            <small>
                                                Lat: {infoWindowPos.lat.toFixed(5)}<br />
                                                Lng: {infoWindowPos.lng.toFixed(5)}
                                            </small>
                                        </div>
                                    </InfoWindow>
                                )}


            }
            {fieldPolygon.length > 0 && (
                <Polygon
                    paths={fieldPolygon}
                    editable={true}
                    onLoad={handlePolygonLoad}
                    onMouseUp={handlePolygonEdited}
                    onDragEnd={handlePolygonEdited}
                    options={{
                        fillColor: "#00FF0088",
                        strokeColor: "#00FF00",
                        strokeWeight: 2
                    }}
                />
            )}
        </GoogleMap>
    ) : (
        <p>Loading map...</p>
    )
}
                </div >

    {/* Side Panel */ }
    < div style = {{
    flex: 1,
        padding: "20px",
            backgroundColor: "#f4f6f8",
                border: "1px solid #ccc",
                    borderRadius: "8px",
                        height: "fit-content"
}}>
    <h3 style={{ color: "#2c3e50", marginBottom: "15px" }}>
        🛰️ Monitoring Panel
    </h3>
{ fieldId && <p><strong>Field ID:</strong> {fieldId}</p> }
{
    amedData ? (
        <ul style={{ paddingLeft: "20px" }}>
            <li><strong>Crop:</strong> {amedData.cropType}</li>
            <li><strong>Stage:</strong> {amedData.cropStage}</li>
            <li><strong>NDVI:</strong> {amedData.ndvi}</li>
            <li><strong>Soil Moisture:</strong> {amedData.soilMoisture}</li>
            <li><strong>Last Update:</strong> {amedData.lastUpdated}</li>
        </ul>
    ) : (
        <p style={{ fontStyle: "italic" }}>
            Click on a field to retrieve AMED data.
        </p>
    )
}
                    {amedData && amedData.ndviHistory && (
                        <div style={{ marginTop: 20 }}>
                            <h5>NDVI Trend (Mock)</h5>
                            <NDVIChart ndviData={amedData.ndviHistory} />
                        </div>
                    )}


                </div >
            </div >
        </div >
    );
};

export default AgriFieldMap;
